import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Calendar,
  CheckCircle2,
  Clock,
  DollarSign,
  Upload,
  FileText
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Tables } from '@/types/database'

type Parcela = Tables<'participante_parcelas'>

interface CarnePagamentoProps {
  parcelas: Parcela[]
  participanteNome: string
  valorEntrada?: number | null // Valor pago na inscrição/entrada
  onMarcarComoPaga?: (parcelaId: string) => void
  onUploadComprovante?: (parcelaId: string) => void
  readonly?: boolean
}

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  pago: {
    label: 'Pago',
    className: 'bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-200',
  },
  pendente: {
    label: 'Pendente',
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-200',
  },
  isento: {
    label: 'Isento',
    className: 'bg-slate-100 text-slate-700 dark:bg-slate-500/10 dark:text-slate-200',
  },
}

const METODO_LABEL: Record<string, string> = {
  pix: 'Pix',
  dinheiro: 'Dinheiro',
  cartao: 'Cartão',
}

export function CarnePagamento({
  parcelas,
  participanteNome,
  valorEntrada = 0,
  onMarcarComoPaga,
  onUploadComprovante,
  readonly = false
}: CarnePagamentoProps) {
  if (!parcelas || parcelas.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Carnê de Pagamento
          </CardTitle>
          <CardDescription>
            Nenhuma parcela encontrada para {participanteNome}
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  // Ordenar parcelas por número
  const parcelasOrdenadas = [...parcelas].sort((a, b) => a.numero_parcela - b.numero_parcela)

  // Calcular estatísticas
  const totalParcelas = parcelas.length
  const parcelasPagas = parcelas.filter(p => p.status === 'pago').length
  const valorTotal = parcelas.reduce((sum, p) => sum + Number(p.valor), 0)
  const valorPago = parcelas.filter(p => p.status === 'pago')
    .reduce((sum, p) => sum + Number(p.valor), 0)

  // Considerar valor de entrada no cálculo de valor pago
  const valorEntradaNum = Number(valorEntrada) || 0
  const valorPagoTotal = valorPago + valorEntradaNum
  const percentualPago = valorTotal > 0 ? (valorPagoTotal / valorTotal) * 100 : 0

  const isParcelaVencida = (vencimento: string, status: string | null) => {
    if (status === 'pago' || status === 'isento') return false
    const dataVencimento = new Date(vencimento)
    const hoje = new Date()
    hoje.setHours(0, 0, 0, 0)
    return dataVencimento < hoje
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          Carnê de Pagamento
        </CardTitle>
        <CardDescription>
          Acompanhamento de pagamento de {participanteNome}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Resumo */}
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="h-4 w-4" />
              Progresso
            </div>
            <div className="mt-2 text-2xl font-bold">
              {parcelasPagas}/{totalParcelas}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {percentualPago.toFixed(0)}% concluído
            </div>
          </div>

          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <DollarSign className="h-4 w-4" />
              Valor Pago
            </div>
            <div className="mt-2 text-2xl font-bold">
              R$ {valorPagoTotal.toFixed(2)}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              de R$ {valorTotal.toFixed(2)}
              {valorEntradaNum > 0 && (
                <span className="ml-1 text-green-600 dark:text-green-400">
                  (+R$ {valorEntradaNum.toFixed(2)} entrada)
                </span>
              )}
            </div>
          </div>

          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              Restante
            </div>
            <div className="mt-2 text-2xl font-bold">
              R$ {(valorTotal - valorPagoTotal).toFixed(2)}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {totalParcelas - parcelasPagas} parcela(s)
            </div>
          </div>
        </div>

        {/* Aviso sobre valor de entrada */}
        {valorEntradaNum > 0 && (
          <div className="rounded-lg bg-green-50 p-4 dark:bg-green-500/10">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-green-900 dark:text-green-100">
                  Valor de Entrada Registrado
                </h4>
                <p className="mt-1 text-sm text-green-700 dark:text-green-200">
                  R$ {valorEntradaNum.toFixed(2)} foi pago na inscrição e será abatido da primeira parcela.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Barra de progresso */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Progresso do pagamento</span>
            <span className="font-medium">{percentualPago.toFixed(0)}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
            <div
              className="h-full bg-primary transition-all duration-500"
              style={{ width: `${percentualPago}%` }}
            />
          </div>
        </div>

        {/* Lista de parcelas */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold">Parcelas</h4>
          <div className="space-y-2">
            {parcelasOrdenadas.map((parcela, index) => {
              const vencida = isParcelaVencida(parcela.vencimento, parcela.status)
              const statusInfo = STATUS_BADGE[parcela.status || 'pendente']
              const isPrimeiraParcela = index === 0
              const valorOriginal = Number(parcela.valor)
              const valorComAbatimento = isPrimeiraParcela && valorEntradaNum > 0
                ? Math.max(0, valorOriginal - valorEntradaNum)
                : valorOriginal

              return (
                <div
                  key={parcela.id}
                  className={cn(
                    'flex flex-col gap-3 rounded-lg border p-4 transition-colors sm:flex-row sm:items-center sm:justify-between',
                    vencida && 'border-destructive/50 bg-destructive/5',
                    isPrimeiraParcela && valorEntradaNum > 0 && 'border-green-500/50 bg-green-50 dark:bg-green-500/10'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                      {parcela.numero_parcela}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          Parcela {parcela.numero_parcela}/{totalParcelas}
                        </span>
                        <Badge className={statusInfo.className}>
                          {statusInfo.label}
                        </Badge>
                        {vencida && (
                          <Badge variant="destructive" className="text-xs">
                            Vencida
                          </Badge>
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Vencimento: {new Date(parcela.vencimento).toLocaleDateString('pt-BR')}
                        </span>
                        <span className="flex items-center gap-1">
                          <DollarSign className="h-3 w-3" />
                          {isPrimeiraParcela && valorEntradaNum > 0 ? (
                            <span className="flex items-center gap-2">
                              <span className="text-muted-foreground line-through">
                                R$ {valorOriginal.toFixed(2)}
                              </span>
                              <span className="font-semibold text-green-600 dark:text-green-400">
                                R$ {valorComAbatimento.toFixed(2)}
                              </span>
                            </span>
                          ) : (
                            <>R$ {valorOriginal.toFixed(2)}</>
                          )}
                        </span>
                        {parcela.metodo && (
                          <span>
                            Método: {METODO_LABEL[parcela.metodo] || parcela.metodo}
                          </span>
                        )}
                      </div>
                      {isPrimeiraParcela && valorEntradaNum > 0 && (
                        <div className="mt-2 rounded-md bg-green-100 p-2 dark:bg-green-500/20">
                          <p className="text-xs text-green-700 dark:text-green-300">
                            💰 Entrada de R$ {valorEntradaNum.toFixed(2)} abatida nesta parcela
                            {valorComAbatimento === 0 && ' - Parcela totalmente quitada pela entrada'}
                          </p>
                        </div>
                      )}
                      {parcela.pago_em && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          Pago em: {new Date(parcela.pago_em).toLocaleDateString('pt-BR')}
                        </div>
                      )}
                      {parcela.observacoes && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          Obs: {parcela.observacoes}
                        </div>
                      )}
                    </div>
                  </div>

                  {!readonly && parcela.status !== 'pago' && (
                    <div className="flex gap-2 sm:flex-col lg:flex-row">
                      {onUploadComprovante && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onUploadComprovante(parcela.id)}
                        >
                          <Upload className="mr-1 h-3 w-3" />
                          Comprovante
                        </Button>
                      )}
                      {onMarcarComoPaga && (
                        <Button
                          size="sm"
                          onClick={() => onMarcarComoPaga(parcela.id)}
                        >
                          <CheckCircle2 className="mr-1 h-3 w-3" />
                          Marcar como Pago
                        </Button>
                      )}
                    </div>
                  )}

                  {parcela.comprovante_url && (
                    <Button
                      size="sm"
                      variant="ghost"
                      asChild
                    >
                      <a href={parcela.comprovante_url} target="_blank" rel="noopener noreferrer">
                        <FileText className="mr-1 h-3 w-3" />
                        Ver Comprovante
                      </a>
                    </Button>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}